import { forwardRef } from 'react';
import type { PaymentRequestData } from '@saiens/shared/types';

interface AcceptanceReportPdfProps {
  data: PaymentRequestData;
}

const PAGE_WIDTH = 794; // A4 at 96 DPI

const styles = {
  page: {
    width: `${PAGE_WIDTH}px`,
    padding: '40px 50px',
    fontFamily: "'Microsoft JhengHei', 'Noto Sans CJK TC', 'PingFang TC', sans-serif",
    fontSize: '13px',
    color: '#1a1a1a',
    backgroundColor: '#fff',
    lineHeight: '1.6',
  } as React.CSSProperties,
  title: {
    textAlign: 'center' as const,
    marginBottom: '20px',
  },
  companyName: {
    fontSize: '22px',
    fontWeight: 700,
    marginBottom: '4px',
    letterSpacing: '2px',
  } as React.CSSProperties,
  docTitle: {
    fontSize: '20px',
    fontWeight: 600,
    letterSpacing: '8px',
  } as React.CSSProperties,
  docNo: {
    fontSize: '12px',
    color: '#666',
    marginTop: '2px',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 700,
    borderBottom: '2px solid #374151',
    paddingBottom: '4px',
    marginBottom: '10px',
    marginTop: '18px',
  } as React.CSSProperties,
  infoTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '14px',
  },
  infoLabel: {
    backgroundColor: '#f0f4ff',
    padding: '6px 10px',
    fontWeight: 600,
    fontSize: '12px',
    border: '1px solid #d1d5db',
    width: '15%',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  infoValue: {
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    fontSize: '13px',
  } as React.CSSProperties,
  noteBox: {
    border: '1px solid #d1d5db',
    padding: '10px 14px',
    marginBottom: '14px',
    fontSize: '12px',
    lineHeight: '1.8',
    minHeight: '40px',
    whiteSpace: 'pre-wrap' as const,
  } as React.CSSProperties,
  scoreTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '11px',
    marginTop: '8px',
  },
  scoreTh: {
    backgroundColor: '#374151',
    color: '#fff',
    padding: '5px 6px',
    fontSize: '10px',
    fontWeight: 600,
    border: '1px solid #374151',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  scoreTd: {
    padding: '4px 6px',
    border: '1px solid #d1d5db',
    fontSize: '11px',
  } as React.CSSProperties,
  scoreTdCenter: {
    padding: '4px 6px',
    border: '1px solid #d1d5db',
    fontSize: '11px',
    textAlign: 'center' as const,
  } as React.CSSProperties,
  scoreCircleFilled: {
    display: 'inline-block',
    width: '18px',
    height: '18px',
    lineHeight: '18px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '10px',
    textAlign: 'center' as const,
    marginRight: '2px',
  } as React.CSSProperties,
  scoreCircleEmpty: {
    display: 'inline-block',
    width: '18px',
    height: '18px',
    lineHeight: '18px',
    borderRadius: '50%',
    backgroundColor: '#f3f4f6',
    color: '#9ca3af',
    fontSize: '10px',
    textAlign: 'center' as const,
    marginRight: '2px',
  } as React.CSSProperties,
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    marginTop: '8px',
  } as React.CSSProperties,
  photoItem: {
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  } as React.CSSProperties,
  photoImg: {
    width: '100%',
    height: '180px',
    objectFit: 'cover' as const,
    display: 'block',
  } as React.CSSProperties,
  photoLabel: {
    fontSize: '10px',
    color: '#6b7280',
    textAlign: 'center' as const,
    padding: '2px',
    backgroundColor: '#f9fafb',
  } as React.CSSProperties,
  sigSection: {
    display: 'flex',
    gap: '20px',
    marginTop: '16px',
    pageBreakInside: 'avoid' as const,
  } as React.CSSProperties,
  sigBox: {
    flex: 1,
    textAlign: 'center' as const,
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '10px',
    minHeight: '120px',
  } as React.CSSProperties,
  sigLabel: {
    fontSize: '12px',
    fontWeight: 600,
    marginBottom: '6px',
  } as React.CSSProperties,
  sigImg: {
    maxHeight: '70px',
    maxWidth: '180px',
  } as React.CSSProperties,
  sigDate: {
    fontSize: '10px',
    color: '#6b7280',
    marginTop: '4px',
  } as React.CSSProperties,
} as const;

export const AcceptanceReportPdf = forwardRef<HTMLDivElement, AcceptanceReportPdfProps>(
  function AcceptanceReportPdf({ data }, ref) {
    return (
      <div ref={ref} style={styles.page}>
        {/* Title */}
        <div style={styles.title}>
          <div style={styles.companyName}>{data.company_name}</div>
          <div style={styles.docTitle}>工程完工自我檢查表</div>
          {data.acceptance_name && (
            <div style={styles.docNo}>{data.acceptance_name}</div>
          )}
        </div>

        {/* Section 1: Basic Info */}
        <div style={styles.sectionTitle}>一、基本資訊</div>
        <table style={styles.infoTable}>
          <tbody>
            <tr>
              <td style={styles.infoLabel}>案名/案編</td>
              <td style={styles.infoValue}>
                {data.project_name || '---'}
                {data.sale_order_name ? ` (${data.sale_order_name})` : ''}
              </td>
              <td style={styles.infoLabel}>客戶名稱</td>
              <td style={styles.infoValue}>{data.partner_name || '---'}</td>
            </tr>
            <tr>
              <td style={styles.infoLabel}>施工地址</td>
              <td style={styles.infoValue} colSpan={3}>
                {data.project_addr || '---'}
              </td>
            </tr>
            <tr>
              <td style={styles.infoLabel}>完工日期</td>
              <td style={styles.infoValue}>{data.completion_date || '---'}</td>
              <td style={styles.infoLabel}>填表人</td>
              <td style={styles.infoValue}>{data.salesperson || '---'}</td>
            </tr>
          </tbody>
        </table>

        {/* Section 2: Photo Method */}
        {data.photo_method_note && (
          <>
            <div style={styles.sectionTitle}>二、完工照片建檔方式</div>
            <div style={styles.noteBox}>
              {data.photo_method_note}
            </div>
          </>
        )}

        {/* Section 3: Inspection Checklist */}
        {data.items.length > 0 && (
          <>
            <div style={styles.sectionTitle}>驗收項目確認表</div>
            <table style={styles.scoreTable}>
              <thead>
                <tr>
                  <th style={{ ...styles.scoreTh, width: '5%' }}>#</th>
                  <th style={{ ...styles.scoreTh, width: '25%' }}>項目</th>
                  <th style={{ ...styles.scoreTh, width: '40%' }}>檢查標準</th>
                  <th style={{ ...styles.scoreTh, width: '10%' }}>確認</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item, idx) => (
                  <tr key={idx}>
                    <td style={styles.scoreTdCenter}>{idx + 1}</td>
                    <td style={styles.scoreTd}>{item.name}</td>
                    <td style={styles.scoreTd}>
                      {item.inspection_standard || ''}
                    </td>
                    <td style={styles.scoreTdCenter}>
                      {item.status === 'not_applicable' ? (
                        <span style={{ color: '#6b7280', fontSize: '10px' }}>未施作</span>
                      ) : item.score > 0 && (
                        <span style={{ color: '#16a34a', fontSize: '16px', fontWeight: 700 }}>✓</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Section 6: Signatures */}
        <div style={styles.sigSection}>
          <div style={styles.sigBox}>
            <div style={styles.sigLabel}>填表人（業務）簽名</div>
            {data.employee_signature_uri ? (
              <>
                <img
                  src={data.employee_signature_uri}
                  alt="employee signature"
                  style={styles.sigImg}
                  crossOrigin="anonymous"
                />
                <div style={styles.sigDate}>
                  {data.employee_signed_date}
                </div>
              </>
            ) : (
              <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '30px' }}>
                尚未簽名
              </div>
            )}
          </div>
        </div>

        {/* Section 7: Selected Photos */}
        {data.photos.length > 0 && (
          <div style={{ pageBreakBefore: 'always' as const }}>
            <div style={styles.sectionTitle}>精選完工照片</div>
            <div style={styles.photoGrid}>
              {data.photos.map((photo, idx) => (
                <div key={idx} style={styles.photoItem}>
                  <img
                    src={photo.data_uri}
                    alt={photo.name}
                    style={styles.photoImg}
                    crossOrigin="anonymous"
                  />
                  <div style={styles.photoLabel}>{photo.name}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  },
);
